import { useContext, useEffect, useMemo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQuery } from '@tanstack/react-query';
import { fetchOfferDocuments } from 'services';
import { AppContext } from 'context/ContextProvider';

import useFetchInvestOffers from './useFetchInvestOffers';
import { campaignsListType } from '../services/fetchCampaignOffers';
import { CampaignsOffersType, InvestOffer } from '../types';

/**
 * Fetches Prismic offer documents + LD campaign offers and returns them merged as
 * `InvestOffer[]`. Shared between the new ForYouChrome (Phase 1 layout) and the
 * deep-link slug lookup at /invest/offer/:slug. React Query dedupes the underlying
 * fetches by key.
 */
export function useMergedInvestOffers(): { offers: InvestOffer[]; isLoading: boolean } {
  const { portalcampaignids, runCampaignOffers } = useFlags<{
    portalcampaignids?: campaignsListType[];
    runCampaignOffers?: boolean;
  }>();
  const { formatter } = useContext(AppContext);

  const [listOfCampaigns, setListOfCampaigns] = useState<campaignsListType[]>([]);

  useEffect(() => {
    setListOfCampaigns(Array.isArray(portalcampaignids) ? [...portalcampaignids] : []);
  }, [portalcampaignids]);

  const { data: prismicData, isLoading: prismicIsLoading } = useQuery({
    queryFn: () => fetchOfferDocuments({ docStatus: 'current' }),
    queryKey: ['fetchOfferDocuments', 'current'],
  });

  const { data: campaignData, isLoading: campaignIsLoading } = useFetchInvestOffers({
    listOfCampaigns,
    page: 0,
    pageSize: 500,
  });

  const offers = useMemo<InvestOffer[]>(() => {
    if (!runCampaignOffers && !prismicData) return [];
    const campaignOffers: InvestOffer[] = runCampaignOffers
      ? (campaignData ?? []).map((x: CampaignsOffersType) => {
          const formatedPrice = formatter.format(Number(x.price?.toFixed(2) || 0));
          return {
            ...x,
            id: x.id,
            type: '',
            name: x.wineName,
            subtitle: `${x.vintage} @ ${formatedPrice} per ${x.unitSize}`,
            priceGbp: x.price,
            price: x?.priceInTargetCurrency ?? x.price,
            unitSize: x.unitSize,
            expiryDate: x.createdDateTime,
            region: x?.region || '',
            status: 'current',
            offerSource: 'LD',
            mainImage: `https://cwcdst2prdctimgimprtfnct.blob.core.windows.net/height/400/${x?.lwin18.substring(
              0,
              7,
            )}.png`,
            sections: [],
            disclaimer: '',
          };
        })
      : [];

    const prismicOffers: InvestOffer[] = (prismicData?.results ?? []).map(
      (x) =>
        ({
          ...x,
          status: x.type,
          offerSource: 'prismic',
          price: x.priceGbp,
        } as unknown as InvestOffer),
    );

    return [...campaignOffers, ...prismicOffers].filter((o) => o.status === 'current');
  }, [campaignData, prismicData, runCampaignOffers, formatter]);

  return { offers, isLoading: prismicIsLoading || (runCampaignOffers && campaignIsLoading) || false };
}
